<template>
    <ul id="access_type_4">
        <template v-if="!item.accessId">
            <li class="form-label">{{ $t('Address Type') }}</li>
            <li>
                <cic-radio-group
                    id="access_address_type"
                    :value="item.accountId"
                    :enabled="editing"
                    :options="[
                        { value: 0, label: $t('Full Email Address') },
                        { value: 1, label: $t('Domain') },
                    ]"
                    @input="(val) => updateFieldValue('accountId', val)"
                />
            </li>
        </template>
        <li class="form-label">{{ $t(item.accountId === 1 ? 'Domain' : 'Email Address') }}</li>
        <li>
            <cic-textbox
                id="access_name"
                :value="item.name"
                :enabled="editing && !item.accessId"
                :maxlength="128"
                @input="(val) => updateFieldValue('name', val)"
            />
        </li>
        <li class="form-label">{{ $t('Uses Same Mailbox As An Existing Access Point') }}</li>
        <li>
            <cic-checkbox
                id="access_no_mailbox"
                :value="item.mailboxes.length === 0"
                :enabled="editing"
                @click="toggleNoMailbox"
            />
        </li>
        <template v-if="item.mailboxes.length">
            <template v-if="newEmail">
                <li class="form-label">{{ $t('Authentication Type') }}</li>
                <li>
                    <cic-select
                        id="access_auth_type"
                        :value="item.mailboxes[0].authType"
                        :enabled="editing"
                        :options="[
                            { value: 0, label: $t('Standard') },
                            { value: 1, label: $t('OAuth2.0 Azure AD') },
                            { value: 2, label: $t('OAuth2.0 Google') },
                        ]"
                        @input="(val) => updateMailbox(0, 'authType', val)"
                    />
                </li>
            </template>
            <template v-if="item.accountId === 1">
                <li class="form-label">{{ $t('End Points') }}</li>
                <li>
                    <table id="tblEndPoints">
                        <thead>
                            <tr>
                                <th v-if="newEmail && item.accessId"></th>
                                <th>{{ $t('User') }}</th>
                                <th v-if="item.mailboxes[0].authType === 0">
                                    {{ $t('Password') }}
                                </th>
                                <th v-else-if="item.accessId && editing">
                                    {{ $t('Reauthorise') }}
                                </th>
                                <th>{{ $t('Main Mailbox') }}</th>
                                <th v-if="newEmail">
                                    {{ $t('Archive Messages') }}
                                </th>
                                <th v-if="editing"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(mailbox, mailboxIndex) in item.mailboxes">
                                <td v-if="newEmail && item.accessId">
                                    <span
                                        v-if="mailbox.failed !== null"
                                        :class="mailbox.failed ? 'inactive' : 'active'"
                                        :title="mailbox.errorMessage"
                                    >
                                    </span>
                                </td>
                                <td>
                                    <cic-textbox
                                        :value="
                                            mailbox.address.indexOf('@') >= 0
                                                ? mailbox.address.substring(0, mailbox.address.indexOf('@'))
                                                : mailbox.address
                                        "
                                        :enabled="editing"
                                        :maxlength="124"
                                        @input="(val) => updateMailbox(mailboxIndex, 'address', val)"
                                    />
                                </td>
                                <td v-if="item.mailboxes[0].authType === 0">
                                    <cic-textbox
                                        :value="mailbox.password"
                                        :enabled="editing"
                                        :maxlength="newEmail ? 64 : 128"
                                        :placeholder="newEmail ? $t('Password') : ''"
                                        :show-password="newEmail"
                                        @input="(val) => updateMailbox(mailboxIndex, 'password', val)"
                                    />
                                </td>
                                <td v-else-if="item.accessId && editing">
                                    <cic-checkbox
                                        v-if="originalAddresses.includes(mailbox.address)"
                                        :value="!!mailbox.reauthorise"
                                        @input="(val) => updateMailbox(mailboxIndex, 'reauthorise', val)"
                                    />
                                </td>
                                <td>
                                    <cic-textbox
                                        :value="mailbox.mainMailbox"
                                        :enabled="editing"
                                        :maxlength="32"
                                        @input="(val) => updateMailbox(mailboxIndex, 'mainMailbox', val)"
                                    />
                                </td>
                                <td v-if="newEmail">
                                    <cic-checkbox
                                        :value="mailbox.archive"
                                        :enabled="editing"
                                        @input="(val) => updateMailbox(mailboxIndex, 'archive', val)"
                                    />
                                </td>
                                <td v-if="editing">
                                    <icon-button
                                        v-if="item.mailboxes.length > 1"
                                        icon="remove-icon"
                                        @click="removeMailbox(mailboxIndex)"
                                    />
                                </td>
                            </tr>
                            <tr v-if="editing">
                                <td
                                    :colspan="
                                        2 +
                                        (newEmail ? 1 : 0) +
                                        (item.mailboxes[0].authType === 0 || item.accessId ? 1 : 0) +
                                        (newEmail && item.accessId ? 1 : 0)
                                    "
                                ></td>
                                <td><icon-button icon="add-icon" @click="addMailbox" /></td>
                            </tr>
                        </tbody>
                    </table>
                </li>
            </template>
            <li class="form-label">{{ $t('Server') }}</li>
            <li>
                <cic-textbox
                    id="access_server"
                    :value="item.mailboxes[0].server"
                    :enabled="editing"
                    :maxlength="64"
                    @input="(val) => updateMailbox(0, 'server', val)"
                />
            </li>
            <li class="form-label">{{ $t('Port') }}</li>
            <li>
                <cic-textbox
                    id="access_port"
                    :value="item.mailboxes[0].port"
                    :enabled="editing"
                    :int-only="true"
                    :maxlength="5"
                    @input="(val) => updateMailbox(0, 'port', val)"
                />
            </li>
            <template v-if="item.accountId !== 1">
                <template v-if="item.mailboxes[0].authType === 0">
                    <li class="form-label">{{ $t('Password') }}</li>
                    <li>
                        <cic-textbox
                            id="access_password"
                            :value="item.mailboxes[0].password"
                            :enabled="editing"
                            :maxlength="newEmail ? 64 : 128"
                            :placeholder="newEmail ? $t('Password') : ''"
                            :show-password="newEmail"
                            @input="(val) => updateMailbox(0, 'password', val)"
                        />
                    </li>
                </template>
                <template
                    v-else-if="
                        editing &&
                        item.accessId &&
                        item.mailboxes[0].authType !== 0 &&
                        item.name === originalName &&
                        item.mailboxes[0].authType === originalAuthType
                    "
                >
                    <li class="form-label">{{ $t('Reauthorise') }}</li>
                    <li>
                        <cic-checkbox
                            id="access_reauth"
                            :value="!!item.mailboxes[0].reauthorise"
                            @input="(val) => updateMailbox(0, 'reauthorise', val)"
                        />
                    </li>
                </template>
                <li class="form-label">{{ $t('Main Mailbox') }}</li>
                <li>
                    <cic-textbox
                        id="access_main"
                        :value="item.mailboxes[0].mainMailbox"
                        :enabled="editing"
                        :maxlength="32"
                        @input="(val) => updateMailbox(0, 'mainMailbox', val)"
                    />
                </li>
                <template v-if="newEmail">
                    <li class="form-label">{{ $t('Archive Messages') }}</li>
                    <li>
                        <cic-checkbox
                            id="access_archive"
                            :value="item.mailboxes[0].archive"
                            :enabled="editing"
                            @input="(val) => updateMailbox(0, 'archive', val)"
                        />
                    </li>
                </template>
            </template>
            <template v-if="!newEmail">
                <li class="form-label">{{ $t('Retries') }}</li>
                <li>
                    <cic-textbox
                        id="access_retries"
                        :value="item.mailboxes[0].retries"
                        :enabled="editing"
                        :int-only="true"
                        @input="(val) => updateMailbox(0, 'retries', val)"
                    />
                </li>
            </template>
            <li class="form-label">{{ $t('IMAP Flags') }}</li>
            <li>
                <cic-textbox
                    id="access_imap"
                    :value="item.mailboxes[0].imapFlags"
                    :enabled="editing"
                    :maxlength="64"
                    @input="(val) => updateMailbox(0, 'imapFlags', val)"
                />
            </li>
        </template>
    </ul>
</template>

<script>
    import IconButton from '@/components/controls/IconButton';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import CicRadioGroup from '@/components/inputs/CicRadioGroup';
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextbox from '@/components/inputs/CicTextbox';

    export default {
        name: 'EditEmailAccessPoint',
        components: {
            IconButton,
            CicCheckbox,
            CicRadioGroup,
            CicSelect,
            CicTextbox,
        },
        props: {
            editing: {
                type: Boolean,
                default: false,
            },
            item: {
                type: Object,
                required: true,
            },
            newEmail: {
                type: Boolean,
                default: false,
            },
        },
        emits: ['change'],
        data() {
            return {
                originalAddresses: [],
                originalName: null,
                originalAuthType: 0,
            };
        },
        created() {
            this.originalAddresses = this.item.mailboxes.map((x) => x.address.substring(0, x.address.indexOf('@')));
            this.originalName = this.item.name;
            this.originalAuthType = this.item.mailboxes.length ? this.item.mailboxes[0].authType : 0;
        },
        methods: {
            addMailbox() {
                const mailboxes = [...this.item.mailboxes];
                mailboxes.push({
                    address: '',
                    password: '',
                    server: '',
                    port: 0,
                    imapFlags: '',
                    retries: 0,
                    mainMailbox: this.$t('INBOX'),
                    archive: false,
                    failed: null,
                    authType: 0,
                    reauthorise: false,
                });
                this.updateFieldValue('mailboxes', mailboxes);
            },

            removeMailbox(index) {
                const mailboxes = [...this.item.mailboxes];
                mailboxes.splice(index, 1);
                this.updateFieldValue('mailboxes', mailboxes);
            },

            toggleNoMailbox(val) {
                if (val) {
                    this.$emit('change', 'mailboxes', []);
                } else {
                    this.addMailbox();
                }
            },

            updateFieldValue(field, value) {
                this.$emit('change', field, value);
            },

            updateMailbox(index, field, val) {
                const mailboxes = [...this.item.mailboxes];
                mailboxes[index][field] = val;
                this.updateFieldValue('mailboxes', mailboxes);
            },
        },
    };
</script>
